import { Trans, useLingui } from "@lingui/react/macro";
import React, { useState } from "react";

import type { MFAMethod } from "../api";

import { ApiConnector, RequestError } from "../api/api-connector";
import { Button } from "../components/ui-kit/button";
import { Dialog } from "../components/ui-kit/dialog";
import { useDialog } from "../components/ui-kit/dialog-provider";
import { PasswordInput } from "../components/ui-kit/fields/password-input";
import { Form } from "../components/ui-kit/form/form";
import { MfaVerification } from "../features/auth/mfa/mfa-verification";

export function PasswordPrompt({ api }: { api: ApiConnector }) {
  const { t } = useLingui();
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<{
    id: string;
    methods: MFAMethod[];
  }>();
  const { alert } = useDialog();

  // The password was only the first factor. Verifying the challenge issues the
  // fresh token, so the waiting request is released from there instead.
  if (challenge) {
    return (
      <Dialog open>
        <MfaVerification
          api={api}
          challengeId={challenge.id}
          methods={challenge.methods}
          onVerified={() => {
            api.completeFreshLogin();
          }}
          onCancel={() => {
            api.cancelPasswordPrompt();
          }}
          cancelLabel={<Trans>Cancel</Trans>}
        />
      </Dialog>
    );
  }

  return (
    <Dialog open>
      <Form
        onSubmit={async () => {
          try {
            const outcome = await api.relogin(password);

            if (outcome.status === "mfa_required") {
              setPassword("");
              setChallenge({
                id: outcome.challenge_id,
                methods: outcome.methods,
              });
            } else if (outcome.status === "mfa_enrollment_required") {
              // Enrolling ends in a brand new session, which would abandon the
              // request this prompt is holding — so it can't happen from here.
              // Only reachable if the grace period ran out mid-session.
              api.cancelPasswordPrompt();
              await alert(
                t`Two-factor authentication has to be set up before you can continue. Please log out and log in again.`,
              );
            }
          } catch (error) {
            if (
              error instanceof RequestError &&
              error.response.status === 401
            ) {
              setPassword("");
              await alert(JSON.stringify(error.data));
            } else {
              throw error;
            }
          }
        }}
      >
        <div className="flex flex-col">
          <div className="mb-2">
            <Trans>Please confirm with your password.</Trans>
          </div>

          <PasswordInput
            autofocus
            value={password}
            onChange={(newValue) => {
              setPassword(newValue);
            }}
          />

          <div className="h-2" />

          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                api.cancelPasswordPrompt();
              }}
            >
              <Trans>Cancel</Trans>
            </Button>

            <Button type="submit">
              <Trans>Confirm</Trans>
            </Button>
          </div>
        </div>
      </Form>
    </Dialog>
  );
}
